import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Youtube from "@tiptap/extension-youtube";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { CodeBlockLowlight } from "@tiptap/extension-code-block-lowlight";
import { createLowlight, common } from "lowlight";
import { useEffect, useState } from "react";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link as LinkIcon,
  Quote,
  Code2,
  Image as ImageIcon,
  Youtube as YoutubeIcon,
  Table as TableIcon,
  Undo2,
  Redo2,
  Minus,
} from "lucide-react";

const lowlight = createLowlight(common);

// Same heuristic as RichContent — if the string starts with a block-level
// HTML tag we treat it as authored HTML.  Otherwise we assume the legacy
// markdown-ish format ("## heading\n\n**bold** paragraphs") and convert it
// to equivalent HTML so TipTap can render it as structured content instead
// of one dumb paragraph.
const looksLikeHtml = (text) =>
  /^\s*<(p|h[1-6]|ul|ol|blockquote|pre|table|figure|div|img|iframe)\b/i.test(text || "");

const escapeHtml = (s) =>
  (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Minimal markdown → HTML converter matching the legacy `renderBody` logic:
//   `## Heading`   → <h2>Heading</h2>
//   `\n\n`         → paragraph break
//   `**bold**`     → <strong>bold</strong>
//   Everything else becomes <p>…</p> with soft line-breaks preserved.
// This is intentionally narrow — anything more complex (lists, images) can
// be re-added by the admin via the toolbar once the post is loaded.
const legacyMarkdownToHtml = (text) => {
  if (!text) return "";
  const blocks = text.split(/\n\n+/);
  return blocks
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("## ")) {
        const label = trimmed.replace(/^##\s+/, "").trim();
        return `<h2>${escapeHtml(label)}</h2>`;
      }
      if (trimmed.startsWith("### ")) {
        const label = trimmed.replace(/^###\s+/, "").trim();
        return `<h3>${escapeHtml(label)}</h3>`;
      }
      // Preserve soft newlines inside a block as <br />.
      const inner = escapeHtml(trimmed)
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br />");
      return `<p>${inner}</p>`;
    })
    .filter(Boolean)
    .join("");
};

const toEditorHtml = (value) => {
  if (!value) return "";
  return looksLikeHtml(value) ? value : legacyMarkdownToHtml(value);
};

// Compact toolbar button.  IMPORTANT: `onMouseDown` calls preventDefault
// so the click never steals focus from ProseMirror — otherwise the FIRST
// keystroke after a toolbar action gets dropped ('bolded' → 'olded').
const TB = ({ onClick, active, disabled, title, children, testId }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testId}
    className={`p-1.5 rounded-md border border-transparent hover:border-border hover:bg-[hsl(var(--secondary))] disabled:opacity-40 disabled:hover:bg-transparent transition-colors ${
      active ? "bg-[hsl(var(--secondary))] border-border text-[hsl(var(--primary))]" : "text-[hsl(var(--foreground))]/80"
    }`}
  >
    {children}
  </button>
);

const Sep = () => <div className="w-px self-stretch bg-border mx-1" />;

/**
 * RichTextEditor — TipTap-based WYSIWYG for admin CMS blog editing.
 *
 * Storage strategy: outputs clean HTML into the `value` field.  The public
 * BlogPost renderer auto-detects HTML vs the legacy Markdown-ish format so
 * old CMS blogs continue rendering untouched — only new/edited posts start
 * using HTML.
 *
 * Props:
 *   value       string   Current HTML (or blank).
 *   onChange    (html:string) => void   Called on every edit.
 *   placeholder string   Optional placeholder shown when empty.
 *   testId      string   Optional data-testid for the editable region.
 *   minHeight   number   Editor min height in px (default 320).
 */
const RichTextEditor = ({ value, onChange, placeholder, testId, minHeight = 320 }) => {
  const [inited, setInited] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Turn off StarterKit's own copies of the marks we register below
        // with custom configuration — avoids "Duplicate extension names"
        // warnings from tiptap v3.
        codeBlock: false,
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: "text-[hsl(var(--primary))] underline decoration-dotted underline-offset-4",
          rel: "noopener noreferrer nofollow",
          target: "_blank",
        },
      }),
      Image.configure({
        HTMLAttributes: { class: "rounded-xl border border-border my-4" },
      }),
      Youtube.configure({
        controls: true,
        nocookie: true,
        HTMLAttributes: { class: "rounded-xl border border-border my-4 mx-auto" },
        width: 640,
        height: 360,
      }),
      Table.configure({ resizable: true, HTMLAttributes: { class: "my-4 border-collapse w-full text-sm" } }),
      TableRow,
      TableHeader.configure({ HTMLAttributes: { class: "border border-border p-2 bg-[hsl(var(--secondary))] text-left" } }),
      TableCell.configure({ HTMLAttributes: { class: "border border-border p-2 align-top" } }),
      CodeBlockLowlight.configure({
        lowlight,
        HTMLAttributes: { class: "rounded-lg bg-[hsl(var(--secondary))] p-4 my-4 text-sm overflow-x-auto font-mono" },
      }),
    ],
    content: toEditorHtml(value),
    editorProps: {
      attributes: {
        class: "prose prose-slate max-w-none dark:prose-invert focus:outline-none px-4 py-3",
        "data-testid": testId || "rte-editable",
        "data-placeholder": placeholder || "",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // TipTap emits '<p></p>' for empty content — normalize to '' so the
      // save button's required-field check still fires correctly.
      onChange?.(html === "<p></p>" ? "" : html);
    },
  });

  // Keep the editor in sync when the parent replaces `value` externally
  // (e.g. auto-translate button, cancel-edit reset). Skip the initial mount
  // because TipTap already renders the initial `content` prop.  Also runs
  // legacy markdown → HTML on the incoming value so switching between old
  // blogs shows structured content, not a raw markdown blob.
  useEffect(() => {
    if (!editor) return;
    if (!inited) {
      setInited(true);
      return;
    }
    const current = editor.getHTML();
    const incoming = toEditorHtml(value);
    if (current !== incoming && incoming !== "<p></p>") {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor, inited]);

  if (!editor) {
    return <div className="border border-border rounded-lg p-3 text-sm text-muted-foreground">Loading editor…</div>;
  }

  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL (leave blank to remove link)", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const promptImage = () => {
    const url = window.prompt("Image URL");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const promptYoutube = () => {
    const url = window.prompt("YouTube URL (full watch link or youtu.be short link)");
    if (url) editor.commands.setYoutubeVideo({ src: url });
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-background" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-border bg-[hsl(var(--secondary))]/40 sticky top-0 z-10">
        <TB testId="rte-bold" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold (Ctrl+B)"><BoldIcon size={15} /></TB>
        <TB testId="rte-italic" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic (Ctrl+I)"><ItalicIcon size={15} /></TB>
        <TB testId="rte-underline" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline (Ctrl+U)"><UnderlineIcon size={15} /></TB>
        <Sep />
        <TB testId="rte-h2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2"><Heading2 size={15} /></TB>
        <TB testId="rte-h3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Heading 3"><Heading3 size={15} /></TB>
        <Sep />
        <TB testId="rte-ul" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bulleted list"><List size={15} /></TB>
        <TB testId="rte-ol" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list"><ListOrdered size={15} /></TB>
        <TB testId="rte-blockquote" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Blockquote"><Quote size={15} /></TB>
        <TB testId="rte-code" onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive("code")} title="Inline code"><Code2 size={15} /></TB>
        <TB testId="rte-codeblock" onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code block (with syntax highlight)"><Code2 size={15} strokeWidth={2.5} /></TB>
        <Sep />
        <TB testId="rte-link" onClick={promptLink} active={editor.isActive("link")} title="Insert / edit link"><LinkIcon size={15} /></TB>
        <TB testId="rte-image" onClick={promptImage} title="Insert image (paste URL)"><ImageIcon size={15} /></TB>
        <TB testId="rte-youtube" onClick={promptYoutube} title="Embed YouTube video"><YoutubeIcon size={15} /></TB>
        <TB testId="rte-table" onClick={insertTable} title="Insert 3×3 table"><TableIcon size={15} /></TB>
        <TB testId="rte-hr" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule"><Minus size={15} /></TB>
        <Sep />
        <TB testId="rte-undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><Undo2 size={15} /></TB>
        <TB testId="rte-redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><Redo2 size={15} /></TB>
      </div>

      {/* Editable area */}
      <div
        style={{ minHeight: `${minHeight}px` }}
        className="max-h-[70vh] overflow-y-auto"
      >
        <EditorContent editor={editor} />
      </div>

      {/* Empty-state placeholder overlay (CSS-only, doesn't interfere with input) */}
      <style>{`
        [data-testid='${testId || "rte-editable"}'] p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: hsl(var(--muted-foreground));
          pointer-events: none;
          height: 0;
        }
        [data-testid='${testId || "rte-editable"}'] table { border-collapse: collapse; width: 100%; }
        [data-testid='${testId || "rte-editable"}'] table td,
        [data-testid='${testId || "rte-editable"}'] table th { border: 1px solid hsl(var(--border)); padding: 8px; }
        [data-testid='${testId || "rte-editable"}'] table th { background: hsl(var(--secondary)); }
        [data-testid='${testId || "rte-editable"}'] blockquote { border-left: 3px solid hsl(var(--accent)); padding-left: 16px; margin: 12px 0; color: hsl(var(--muted-foreground)); font-style: italic; }
        [data-testid='${testId || "rte-editable"}'] pre { background: hsl(var(--secondary)); padding: 12px; border-radius: 8px; overflow-x: auto; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; }
        [data-testid='${testId || "rte-editable"}'] code { background: hsl(var(--secondary)); padding: 2px 6px; border-radius: 4px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.9em; }
        [data-testid='${testId || "rte-editable"}'] pre code { background: transparent; padding: 0; }
        [data-testid='${testId || "rte-editable"}'] h2 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 1.75rem; margin-top: 1.5em; margin-bottom: 0.5em; }
        [data-testid='${testId || "rte-editable"}'] h3 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 1.4rem; margin-top: 1.25em; margin-bottom: 0.4em; }
        [data-testid='${testId || "rte-editable"}'] ul { list-style: disc; padding-left: 1.5em; }
        [data-testid='${testId || "rte-editable"}'] ol { list-style: decimal; padding-left: 1.5em; }
        [data-testid='${testId || "rte-editable"}'] a { color: hsl(var(--primary)); text-decoration: underline; text-decoration-style: dotted; text-underline-offset: 4px; }
        /* Syntax highlighting — atom-one-dark inspired palette. */
        [data-testid='${testId || "rte-editable"}'] .hljs-comment,
        [data-testid='${testId || "rte-editable"}'] .hljs-quote { color: #6a737d; font-style: italic; }
        [data-testid='${testId || "rte-editable"}'] .hljs-keyword,
        [data-testid='${testId || "rte-editable"}'] .hljs-selector-tag { color: #d73a49; }
        [data-testid='${testId || "rte-editable"}'] .hljs-string,
        [data-testid='${testId || "rte-editable"}'] .hljs-attr { color: #032f62; }
        [data-testid='${testId || "rte-editable"}'] .hljs-number,
        [data-testid='${testId || "rte-editable"}'] .hljs-literal { color: #005cc5; }
        [data-testid='${testId || "rte-editable"}'] .hljs-title,
        [data-testid='${testId || "rte-editable"}'] .hljs-name { color: #6f42c1; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
